import React from 'react';
import { motion } from 'framer-motion';
import { StarIcon, TrendingUpIcon, CheckCircleIcon, ArrowUpIcon } from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { ProgressBar } from '../../components/ui/ProgressBar';
import { useAuth } from '../../lib/AuthContext';
import { formatVND, getTierNameVi } from '../../lib/formatters';
import { TIER_CONFIGS, MOCK_POINTS_TRANSACTIONS } from '../../lib/mockData';
import type { TierLevel } from '../../lib/types';
export function PointsTierPage() {
  const {
    currentCustomer,
    isCustomerLoggedIn
  } = useAuth();
  if (!isCustomerLoggedIn || !currentCustomer) {
    return <div className="max-w-4xl mx-auto px-4 py-16 text-center" data-id="element-1421">
        <StarIcon className="w-16 h-16 text-[#666666] mx-auto mb-4" data-id="element-1422" />
        <h2 className="text-xl font-semibold text-white mb-2" data-id="element-1423">
          Vui lòng đăng nhập
        </h2>
        <p className="text-[#B0B0B0]" data-id="element-1424">
          Đăng nhập để xem điểm và hạng thành viên
        </p>
      </div>;
  }
  const currentTierConfig = TIER_CONFIGS.find(t => t.level === currentCustomer.tier)!;
  const currentTierIndex = TIER_CONFIGS.findIndex(t => t.level === currentCustomer.tier);
  const nextTier = currentTierIndex < TIER_CONFIGS.length - 1 ? TIER_CONFIGS[currentTierIndex + 1] : null;
  const pointsToNext = nextTier ? nextTier.minPoints - currentCustomer.points : 0;
  const progressMax = nextTier ? nextTier.minPoints - currentTierConfig.minPoints : 1;
  const progressValue = nextTier ? currentCustomer.points - currentTierConfig.minPoints : progressMax;
  const recentPoints = MOCK_POINTS_TRANSACTIONS.filter(t => t.customerId === currentCustomer.id).slice(0, 5);
  return <div className="max-w-4xl mx-auto px-4 py-8" data-id="element-1425">
      <motion.div initial={{
      opacity: 0,
      y: 12
    }} animate={{
      opacity: 1,
      y: 0
    }} data-id="element-1426">
        <h1 className="text-2xl font-bold text-white mb-6 flex items-center gap-3" data-id="element-1427">
          <StarIcon className="w-7 h-7 text-[#C8A84E]" data-id="element-1428" />
          Điểm & Hạng Thành Viên
        </h1>
      </motion.div>

      {/* Current Status */}
      <motion.div initial={{
      opacity: 0,
      y: 12
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.05
    }} className="mb-8" data-id="element-1429">
        <div className="bg-gradient-to-br from-[#1A1A1A] to-[#141414] border border-[#2A2A2A] rounded-xl p-6" data-id="element-1430">
          <div className="flex flex-col md:flex-row md:items-center gap-6" data-id="element-1431">
            <div className="flex items-center gap-4" data-id="element-1432">
              <div className="w-16 h-16 rounded-xl flex items-center justify-center text-3xl" style={{
              backgroundColor: `${currentTierConfig.color}15`
            }} data-id="element-1433">
                {currentTierConfig.icon}
              </div>
              <div data-id="element-1434">
                <p className="text-sm text-[#666666]" data-id="element-1435">Hạng hiện tại</p>
                <p className="text-2xl font-bold" style={{
                color: currentTierConfig.color
              }} data-id="element-1436">
                  {currentTierConfig.nameVi}
                </p>
              </div>
            </div>

            <div className="flex-1" data-id="element-1437">
              <div className="flex items-center gap-4 mb-3" data-id="element-1438">
                <div data-id="element-1439">
                  <p className="text-sm text-[#666666]" data-id="element-1440">Điểm tích lũy</p>
                  <p className="text-xl font-bold text-white" data-id="element-1441">
                    {currentCustomer.points.toLocaleString('vi-VN')} điểm
                  </p>
                </div>
                {nextTier && <div className="text-right ml-auto" data-id="element-1442">
                    <p className="text-sm text-[#666666]" data-id="element-1443">Cần thêm</p>
                    <p className="text-lg font-semibold text-[#C8A84E]" data-id="element-1444">
                      {pointsToNext.toLocaleString('vi-VN')} điểm
                    </p>
                  </div>}
              </div>

              {nextTier ? <div data-id="element-1445">
                  <ProgressBar value={progressValue} max={progressMax} showValue={false} size="md" data-id="element-1446" />
                  <div className="flex items-center justify-between mt-1" data-id="element-1447">
                    <span className="text-xs text-[#666666]" data-id="element-1448">
                      {currentTierConfig.nameVi}
                    </span>
                    <span className="text-xs" style={{
                  color: nextTier.color
                }} data-id="element-1449">
                      {nextTier.nameVi} (
                      {nextTier.minPoints.toLocaleString('vi-VN')} điểm)
                    </span>
                  </div>
                </div> : <p className="text-sm text-[#22C55E] flex items-center gap-1" data-id="element-1450">
                  <CheckCircleIcon className="w-4 h-4" data-id="element-1451" />
                  Bạn đã đạt hạng cao nhất!
                </p>}
            </div>
          </div>

          {/* Current Benefits */}
          <div className="mt-6 pt-4 border-t border-[#2A2A2A]" data-id="element-1452">
            <p className="text-sm font-medium text-[#B0B0B0] mb-2" data-id="element-1453">
              Quyền lợi của bạn:
            </p>
            <div className="flex flex-wrap gap-2" data-id="element-1454">
              <Badge variant="gold" size="md" data-id="element-1455">
                Tích điểm x{currentTierConfig.pointMultiplier}
              </Badge>
              <Badge variant="gold" size="md" data-id="element-1456">
                Đổi điểm → tín dụng mua hàng
              </Badge>
              {currentTierConfig.hasBirthdayGift && <Badge variant="gold" size="md" data-id="element-1457">
                  Quà sinh nhật
                </Badge>}
              {currentTierConfig.hasExclusivePromos && <Badge variant="gold" size="md" data-id="element-1458">
                  Ưu đãi riêng
                </Badge>}
            </div>
          </div>
        </div>
      </motion.div>

      {/* Earning Rule */}
      <motion.div initial={{
      opacity: 0,
      y: 12
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.1
    }} className="mb-8" data-id="element-1459">
        <Card className="bg-[#C8A84E]/5 border-[#C8A84E]/20" data-id="element-1460">
          <div className="flex items-center gap-3" data-id="element-1461">
            <TrendingUpIcon className="w-5 h-5 text-[#C8A84E]" data-id="element-1462" />
            <div data-id="element-1463">
              <p className="text-sm font-medium text-white" data-id="element-1464">
                Quy tắc tích điểm
              </p>
              <p className="text-sm text-[#B0B0B0]" data-id="element-1465">
                Mỗi{' '}
                <span className="text-[#C8A84E] font-semibold" data-id="element-1466">100.000đ</span>{' '}
                chi tiêu ={' '}
                <span className="text-[#C8A84E] font-semibold" data-id="element-1467">1 điểm</span>
                {currentTierConfig.pointMultiplier > 1 && <span data-id="element-1468">
                    {' '}
                    (x{currentTierConfig.pointMultiplier} cho hạng{' '}
                    {currentTierConfig.nameVi})
                  </span>}
                {' • '}
                <span className="text-[#22C55E] font-semibold" data-id="element-1469">
                  1.000 điểm = 500.000đ
                </span>{' '}
                tín dụng mua hàng
              </p>
            </div>
          </div>
        </Card>
      </motion.div>

      {/* Tier Comparison */}
      <motion.div initial={{
      opacity: 0,
      y: 12
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.15
    }} className="mb-8" data-id="element-1470">
        <Card title="So Sánh Hạng Thành Viên" data-id="element-1471">
          <div className="overflow-x-auto -mx-5" data-id="element-1472">
            <table className="w-full min-w-[600px]" data-id="element-1473">
              <thead data-id="element-1474">
                <tr className="border-b border-[#2A2A2A]" data-id="element-1475">
                  <th className="text-left text-xs font-semibold text-[#666666] uppercase px-5 py-3" data-id="element-1476">
                    Quyền lợi
                  </th>
                  {TIER_CONFIGS.map(tier => <th key={tier.level} className={`text-center text-xs font-semibold uppercase px-3 py-3 ${tier.level === currentCustomer.tier ? 'text-[#C8A84E]' : 'text-[#666666]'}`} data-id="element-1477">
                      <span className="text-lg mr-1" data-id="element-1478">{tier.icon}</span>
                      {tier.nameVi}
                      {tier.level === currentCustomer.tier && <span className="block text-[10px] text-[#C8A84E] mt-0.5" data-id="element-1479">
                          Hiện tại
                        </span>}
                    </th>)}
                </tr>
              </thead>
              <tbody className="text-sm" data-id="element-1480">
                <tr className="border-b border-[#2A2A2A]/50" data-id="element-1481">
                  <td className="px-5 py-3 text-[#B0B0B0]" data-id="element-1482">Điểm yêu cầu</td>
                  {TIER_CONFIGS.map(tier => <td key={tier.level} className="text-center px-3 py-3 text-white" data-id="element-1483">
                      {tier.minPoints.toLocaleString('vi-VN')}
                      {tier.maxPoints ? ` – ${tier.maxPoints.toLocaleString('vi-VN')}` : '+'}
                    </td>)}
                </tr>
                <tr className="border-b border-[#2A2A2A]/50" data-id="element-1484">
                  <td className="px-5 py-3 text-[#B0B0B0]" data-id="element-1485">Hệ số điểm</td>
                  {TIER_CONFIGS.map(tier => <td key={tier.level} className="text-center px-3 py-3 text-white" data-id="element-1486">
                      x{tier.pointMultiplier}
                    </td>)}
                </tr>
                <tr className="border-b border-[#2A2A2A]/50" data-id="element-1487">
                  <td className="px-5 py-3 text-[#B0B0B0]" data-id="element-1488">
                    Đổi điểm → tín dụng
                  </td>
                  {TIER_CONFIGS.map(tier => <td key={tier.level} className="text-center px-3 py-3 text-[#22C55E]" data-id="element-1489">
                      ✓
                    </td>)}
                </tr>
                <tr className="border-b border-[#2A2A2A]/50" data-id="element-1490">
                  <td className="px-5 py-3 text-[#B0B0B0]" data-id="element-1491">Quà sinh nhật</td>
                  {TIER_CONFIGS.map(tier => <td key={tier.level} className="text-center px-3 py-3" data-id="element-1492">
                      {tier.hasBirthdayGift ? <CheckCircleIcon className="w-4 h-4 text-[#22C55E] mx-auto" data-id="element-1493" /> : <span className="text-[#666666]" data-id="element-1494">—</span>}
                    </td>)}
                </tr>
                <tr data-id="element-1495">
                  <td className="px-5 py-3 text-[#B0B0B0]" data-id="element-1496">Ưu đãi riêng</td>
                  {TIER_CONFIGS.map(tier => <td key={tier.level} className="text-center px-3 py-3" data-id="element-1497">
                      {tier.hasExclusivePromos ? <CheckCircleIcon className="w-4 h-4 text-[#22C55E] mx-auto" data-id="element-1498" /> : <span className="text-[#666666]" data-id="element-1499">—</span>}
                    </td>)}
                </tr>
              </tbody>
            </table>
          </div>
        </Card>
      </motion.div>

      {/* Recent Points */}
      <motion.div initial={{
      opacity: 0,
      y: 12
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.2
    }} data-id="element-1500">
        <Card title="Hoạt Động Điểm Gần Đây" data-id="element-1501">
          {recentPoints.length === 0 ? <p className="text-center py-6 text-[#666666]" data-id="element-1502">
              Chưa có hoạt động điểm
            </p> : <div className="space-y-3" data-id="element-1503">
              {recentPoints.map((pt, i) => <motion.div key={pt.id} initial={{
            opacity: 0,
            x: -10
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: i * 0.03
          }} className="flex items-center gap-3 py-2 border-b border-[#2A2A2A]/50 last:border-0" data-id="element-1504">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${pt.type === 'earn' ? 'bg-[#22C55E]/10' : pt.type === 'redeem' ? 'bg-[#EF4444]/10' : 'bg-[#C8A84E]/10'}`} data-id="element-1505">
                    {pt.type === 'earn' ? <ArrowUpIcon className="w-4 h-4 text-[#22C55E]" data-id="element-1506" /> : <ArrowUpIcon className="w-4 h-4 text-[#EF4444] rotate-180" data-id="element-1507" />}
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-1508">
                    <p className="text-sm text-white truncate" data-id="element-1509">
                      {pt.description}
                    </p>
                    <p className="text-xs text-[#666666]" data-id="element-1510">
                      {new Date(pt.createdAt).toLocaleDateString('vi-VN')}
                    </p>
                  </div>
                  <span className={`text-sm font-semibold flex-shrink-0 ${pt.points >= 0 ? 'text-[#22C55E]' : 'text-[#EF4444]'}`} data-id="element-1511">
                    {pt.points >= 0 ? '+' : ''}
                    {pt.points} điểm
                  </span>
                </motion.div>)}
            </div>}
        </Card>
      </motion.div>
    </div>;
}